import type { CSSProperties } from "react";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { normalizeEmployeeCalendarColor } from "../../../staff/employee/addemployee/employeeFormShared";
import {
  calendarSlotFill,
  formatAppointmentPurpose,
  isLightCssColor,
  purposeColorBackground,
  purposeColorFromItem,
} from "../../viewappointment/components/appointmentViewShared";

type OffCalendarVariant = "walkin" | "other";

const VARIANT_UI: Record<
  OffCalendarVariant,
  {
    title: string;
    subtitle: string;
    Icon: typeof DirectionsWalkIcon;
    accent: string;
    accentSoft: string;
    accentRing: string;
    accentText: string;
    defaultColor: string;
  }
> = {
  walkin: {
    title: "Walk-in",
    subtitle: "Arrived without a booked slot",
    Icon: DirectionsWalkIcon,
    accent: "#0f766e",
    accentSoft: "bg-teal-50",
    accentRing: "ring-teal-200/80",
    accentText: "text-teal-800",
    defaultColor: "#0f766e",
  },
  other: {
    title: "Other",
    subtitle: "Other bookings",
    Icon: MoreHorizIcon,
    accent: "#0369a1",
    accentSoft: "bg-sky-50",
    accentRing: "ring-sky-200/80",
    accentText: "text-sky-800",
    defaultColor: "#0369a1",
  },
};

function initials(name?: string): string {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

export default function WalkInAppointments({
  items,
  colorByPurpose,
  onSelect,
  variant = "walkin",
}: {
  items: any[];
  colorByPurpose?: boolean;
  onSelect: (appointment: any) => void;
  variant?: OffCalendarVariant;
}) {
  if (!items?.length) return null;

  const ui = VARIANT_UI[variant];
  const Icon = ui.Icon;

  return (
    <section
      className={`flex-1 min-w-0 overflow-hidden rounded-2xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/80 shadow-[0_10px_40px_-24px_rgba(15,23,42,0.35)] ring-1 ${ui.accentRing}`}
    >
      <header className="relative flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3.5">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-1"
          style={{ backgroundColor: ui.accent }}
          aria-hidden
        />
        <div className="flex items-start gap-3 min-w-0 pl-1">
          <div
            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${ui.accentSoft}`}
            style={{ color: ui.accent }}
          >
            <Icon sx={{ fontSize: 22 }} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h5 className={`text-[15px] font-semibold tracking-tight ${ui.accentText}`}>
                {ui.title}
              </h5>
              <span
                className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-md px-1.5 text-[11px] font-semibold text-white"
                style={{ backgroundColor: ui.accent }}
              >
                {items.length}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">{ui.subtitle}</p>
          </div>
        </div>
      </header>

      <div className="p-3">
        <div className="grid grid-cols-1 gap-2.5 max-h-[260px] overflow-y-auto pr-0.5 sm:grid-cols-2">
          {items.map((event) => {
            const appointment = {
              ...event?.data?.appointment,
              _eventStart: event?.start,
              _eventEnd: event?.end,
            };
            const purpose = formatAppointmentPurpose(appointment);
            const purposeColor = purposeColorFromItem(appointment);
            const doctorColor =
              normalizeEmployeeCalendarColor(appointment?.color) ||
              appointment?.color;
            const accentColor =
              appointment?.status === "completed"
                ? "#f9a8d4"
                : colorByPurpose && purposeColor
                  ? purposeColor
                  : doctorColor || ui.defaultColor;
            const fillSource =
              appointment?.status === "completed"
                ? "#fce7f3"
                : accentColor;
            const softBg = calendarSlotFill(fillSource, 22);
            const avatarBg = isLightCssColor(accentColor)
              ? purposeColorBackground(accentColor, 72)
              : accentColor;
            const avatarText = isLightCssColor(accentColor)
              ? "#334155"
              : "#ffffff";
            const chipText = avatarText;
            const timeLabel = [appointment?.startTime, appointment?.endTime]
              .filter(Boolean)
              .join(" – ");
            const key = String(
              appointment?.id ?? appointment?._id ?? event?._id ?? Math.random()
            );
            const name = appointment?.patientName || "Patient";

            return (
              <button
                key={key}
                type="button"
                onClick={() => onSelect(appointment)}
                className="group relative overflow-hidden rounded-xl border border-slate-200/90 bg-white text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_12px_28px_-16px_rgba(15,23,42,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={
                  {
                    backgroundColor: softBg,
                  } as CSSProperties
                }
                title={[name, appointment?.dentist, purpose !== "N/A" ? purpose : null]
                  .filter(Boolean)
                  .join(" · ")}
              >
                <span
                  className="absolute inset-y-0 left-0 w-[3px] transition-all group-hover:w-1"
                  style={{ backgroundColor: accentColor }}
                  aria-hidden
                />

                <div className="flex items-center gap-2.5 p-2.5 pl-3.5 min-w-0">
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tracking-wide shadow-sm"
                    style={{ backgroundColor: avatarBg, color: avatarText }}
                  >
                    {initials(name)}
                  </div>

                  <div className="min-w-0 flex-1 flex items-center gap-1.5 overflow-hidden">
                    <p className="truncate text-[13px] font-semibold text-slate-900 shrink min-w-0 max-w-[42%]">
                      {name}
                    </p>
                    {appointment?.regNo ? (
                      <span className="shrink-0 text-[11px] font-medium text-slate-500 tabular-nums">
                        {appointment.regNo}
                      </span>
                    ) : null}
                    {purpose && purpose !== "N/A" ? (
                      <span
                        className="inline-flex shrink-0 items-center truncate max-w-[5.5rem] rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                        style={{ backgroundColor: accentColor, color: chipText }}
                      >
                        {purpose}
                      </span>
                    ) : (
                      <span className="inline-flex shrink-0 items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                        {ui.title}
                      </span>
                    )}
                    {timeLabel ? (
                      <span className="inline-flex shrink-0 items-center rounded-md bg-white/80 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200/80 whitespace-nowrap">
                        {timeLabel}
                      </span>
                    ) : null}
                    {appointment?.dentist ? (
                      <span className="truncate text-[11px] text-slate-500 min-w-0">
                        {appointment.dentist}
                      </span>
                    ) : null}
                  </div>

                  <ChevronRightIcon
                    sx={{ fontSize: 18 }}
                    className="shrink-0 text-slate-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-slate-500"
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
